import React, { useState } from 'react';
import { MapPin, Truck, Clock, CheckCircle, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

interface PincodeCheckerProps {
  productId?: string;
  quantity?: number;
}

interface DeliveryInfo {
  pincode: string;
  serviceable: boolean;
  estimatedDelivery?: string;
  shippingCharge?: number;
  codCharge?: number;
  codAvailable?: boolean;
  codUnavailableReason?: string | null;
  couriers?: { courierName: string; rate: number; estimatedDeliveryDays?: string }[];
}

const PincodeChecker: React.FC<PincodeCheckerProps> = ({ productId, quantity = 1 }) => {
  const [pincode, setPincode] = useState('');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<DeliveryInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleCheck = async () => {
    if (!pincode || pincode.length !== 6 || !/^\d{6}$/.test(pincode)) {
      setError('Please enter a valid 6-digit PIN code');
      setResult(null);
      return;
    }
    if (!productId) {
      setError('Product not loaded');
      return;
    }

    setError(null);
    setChecking(true);
    setResult(null);

    try {
      const res = await fetch(`${API_URL}/shipping/check-pincode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pincode, productId, quantity, cod: true }),
      });
      const data = await res.json();
      if (data.success && data.data) {
        setResult({
          pincode,
          serviceable: data.data.serviceable,
          estimatedDelivery: data.data.estimatedDelivery,
          shippingCharge: data.data.shippingCharge,
          codCharge: data.data.codCharge,
          codAvailable: data.data.codAvailable,
          codUnavailableReason: data.data.codUnavailableReason,
          couriers: data.data.couriers,
        });
      } else {
        setError(data.message || 'Delivery not available');
      }
    } catch {
      setError('Could not check delivery. Try again.');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="space-y-2 overflow-hidden w-full min-w-0">
      <label className="block text-xs sm:text-xs font-bold text-gray-800">Check Delivery</label>
      <div className="flex gap-2 overflow-hidden">
        <div className="relative flex-1 min-w-0">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 flex-shrink-0" />
          <input
            type="text"
            maxLength={6}
            value={pincode}
            onChange={(e) => {
              setPincode(e.target.value.replace(/\D/g, ''));
              setError(null);
            }}
            onKeyDown={(e) => e.key === 'Enter' && handleCheck()}
            placeholder="Enter PIN code"
            className="w-full pl-10 pr-3 py-2.5 sm:py-2 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none min-w-0 bg-white"
          />
        </div>
        <button
          type="button"
          onClick={handleCheck}
          disabled={checking || !productId}
          className="px-4 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 disabled:opacity-50"
        >
          {checking ? '…' : 'Check'}
        </button>
      </div>

      {error && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <XCircle className="w-3.5 h-3.5" /> {error}
        </p>
      )}

      {result && result.serviceable && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-3 text-xs space-y-1.5">
          <p className="flex items-center gap-1.5 text-green-800 font-semibold">
            <CheckCircle className="w-4 h-4" /> Delivery available to {result.pincode}
          </p>
          {result.estimatedDelivery && (
            <p className="flex items-center gap-1.5 text-gray-700">
              <Clock className="w-3.5 h-3.5" /> {result.estimatedDelivery}
            </p>
          )}
          {result.shippingCharge != null && (
            <p className="flex items-center gap-1.5 text-gray-700">
              <Truck className="w-3.5 h-3.5" /> Shipping from ₹{result.shippingCharge}
              {result.codAvailable && result.codCharge ? ` · COD fee ₹${result.codCharge}` : ''}
            </p>
          )}
          {result.codAvailable === false && (
            <p className="text-amber-800">
              {result.codUnavailableReason || 'Cash on Delivery unavailable for this product.'}
            </p>
          )}
        </div>
      )}

      {result && !result.serviceable && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <XCircle className="w-3.5 h-3.5" /> Not serviceable for this pincode
        </p>
      )}
    </div>
  );
};

export default PincodeChecker;
